import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Header from './components/shared/Header';
import ScrollToTop from './ScrollToTop';
import Breadcrumb from './components/breadcrumb/Breadcrumb';
import { AppProvider, useAppContext } from './AppContext';
import { WebService } from './services/WebService';
import { UserService } from './services/UserService';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import PrivateRoute from './components/PrivateRoute';
import { CircularProgress } from '@mui/material';
import { Bounce, ToastContainer } from 'react-toastify';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import i18n from './locales/i18n';
import { getOrCreateBrowserId } from './utils/browserId';
import { signOut } from 'firebase/auth';
import { showErrorToast } from './components/Toastify/toastHelper';
import LoadingComponent from './components/lazy/LoadingComponent';
import * as LazyComponents from './components/lazy/LazyComponents';
import { useTranslation } from 'react-i18next';
import ForgotPassword from './components/auth/ForgotPassword';
import AuthModal from './components/header/AuthModal';
import LogoutConfirmationModal from './components/header/LogoutConfirmationModal';

const auth = getAuth();

const AppContent: React.FC = () => {
    const [selectedTab, setSelectedTab] = useState('');
    const [authLoading, setAuthLoading] = useState(true);
    const [userDataLoading, setUserDataLoading] = useState(true);
    const { role, setWebSettings, setUser, setRole, setPublishedBooksCount, language, showModal, setShowModal, modalMode, setModalMode, showLogoutModal, setShowLogoutModal, logout } = useAppContext();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isRTL, setIsRTL] = useState<boolean>(language === 'AR');

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            showErrorToast(t('auth.loggedOutDueToAnotherLogin'), t);
            navigate('/');
            if (showModal) {
                setShowModal(false);
            }
        } catch (error) {
            showErrorToast(t('auth.logoutFailed'), t);
        }
    };

    useEffect(() => {
        // Only start polling if user is authenticated and user data is loaded
        if (!auth.currentUser || authLoading || userDataLoading) {
            return;
        }

        const browserId = getOrCreateBrowserId();
        console.log('Starting multi-session polling with browserId:', browserId);
        
        // Ensure browser UUID is set on the backend when app starts with authenticated user
        const ensureBrowserIdSet = async () => {
            try {
                await UserService.setUUID(browserId);
                console.log('Browser UUID set for existing session');
            } catch (error) {
                console.error('Failed to set browser UUID:', error);
            }
        };
      
        const poll = async () => {
            if (!auth.currentUser) return;
            console.log('Polling user with browserId:', browserId);
            await UserService.poll(browserId).then((res) =>  {
                console.log('Poll response:', res); // Add debugging
                if (res && !res.valid) {
                    console.log('Multi-session detected, logging out'); // Add debugging
                    handleSignOut();
                }
            }).catch((err) => {
                console.error('Error polling user:', err);
            });
        };
      
        let intervalId: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;
        
        // Set browser ID first, then start polling after a delay
        ensureBrowserIdSet().then(() => {
            // Wait 3 seconds before starting polling to ensure setUUID completes
            timeoutId = setTimeout(() => {
                poll();
                intervalId = setInterval(poll, 15000);
            }, 3000);
        });
      
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
        };
    }, [authLoading, userDataLoading]); // Re-run when auth/user loading states change
      

    useEffect(() => {
      const langMap = {
        EN: 'en',
        FR: 'fr',
        AR: 'ar',
      } as const;
      const langCode = langMap[language as keyof typeof langMap];
      
      // Update RTL state
      const newIsRTL = langCode === 'ar';
      setIsRTL(newIsRTL);
      
      // Ensure i18n is ready before changing language
      if (i18n.isInitialized) {
        i18n.changeLanguage(langCode).catch(() => {
          
        });
      }
      
      const dir = langCode === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.setAttribute('dir', dir);
    }, [language]);

    useEffect(() => {
        console.log("calling location", location);
        switch (location.pathname) {
            case '/':
            case '/home':
                setSelectedTab('home');
                break;
            case '/book-store':
                setSelectedTab('bookStore');
                break;
            case '/reports':
                setSelectedTab('reports');
                break;
            case '/faqs':
                setSelectedTab('faqs');
                break;
            case '/support':
                setSelectedTab('support');
                break;
            case '/contact-us':
                setSelectedTab('contactUs');
                break;
            case '/privacy-policies':
                setSelectedTab('privacyPolicies');
                break;
            case '/terms-and-conditions':
                setSelectedTab('termsAndConditions');
                break;
            case '/purchases':
                setSelectedTab('purchases');
                break;
            case '/profile':
                if(location.search.includes('Library')){
                    setSelectedTab('profile?page=My Library');
                } else { 
                    setSelectedTab('');
                }
                // setSelectedTab('profile?page=My Library');
                break;
            default:
                setSelectedTab('')

        }
    }, [location]);


    useEffect(() => {
        try {
            WebService.GetWebSettings().then((res) => {
                console.log(res);
                setWebSettings({
                    ...res.webSettings,
                    highestSalePriceBook: res.highestSalePriceBook
                });
                setPublishedBooksCount(res.publishedBooksCount);
            }).catch((err: any) => {
                console.error('Error fetching web settings:', err);
            });
        } catch (err: any) {
            console.error('Error in web settings effect:', err);
        }
    }, []);

    const fetchUserFromBackend = async () => {
        try {
            // setUserDataLoading(true);
            const response = await UserService.getUser();
            console.log('response', response);
            setUser(response);
            setRole(response.role);
            return response;
        } catch (err: any) {
            console.error('Error fetching user data:', err);
            return null;
        } finally {
            setUserDataLoading(false);
        }
    };
 
    useEffect(() => {
        let isMounted = true;
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                if (isMounted) {
                    await fetchUserFromBackend();
                }
            } else {
                if (isMounted) {
                    setUser(null);
                    setUserDataLoading(false);
                }
            }
            if (isMounted) {
                setAuthLoading(false);
            }
        });
    
        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    if (authLoading || userDataLoading) {
        return <div className='flex w-full h-screen items-center justify-center'><CircularProgress/></div>;
    }

    return (
        <div className="flex flex-col h-screen w-full">
            {
                !location.pathname.startsWith('/read-book/') && (
                    <>
                        <Header selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
                        <Breadcrumb />
                    </>
                )
            }
            <div className="flex-grow relative">
                <Suspense fallback={<LoadingComponent />}>
                    <Routes>
                        <Route path="/profile" element={<LazyComponents.Profile />} />
                        <Route path="/" element={<LazyComponents.Home />} />
                        <Route path="/home" element={<LazyComponents.Home />} />
                        {role == 'publisher' && <Route path="/purchases" element={<LazyComponents.Purchases />} />}
                        {role == 'publisher' && <Route path="/reports" element={<LazyComponents.Reports />} />}
                        {role == 'publisher' && <Route path="/support" element={<LazyComponents.Support />} />}
                        <Route path="/book-store" element={<LazyComponents.BookStore />} />
                        <Route path="/book/:id" element={<LazyComponents.BookDetails />} />
                        <Route path="/read-book/:id" element={<LazyComponents.Book />} />
                        <Route path="/faqs" element={<LazyComponents.Faqs />} />
                        <Route path="/contact-us" element={<LazyComponents.ContactUs />} />
                        <Route path="/privacy-policies" element={<LazyComponents.PrivacyPolicies />} />
                        <Route path="/terms-and-conditions" element={<LazyComponents.TermsAndConditions />} />
                        <Route path="/support" element={<LazyComponents.Support />} />
                        <Route path="/shopping-cart" element={
                            <PrivateRoute>
                                <LazyComponents.ShoppingCart />
                            </PrivateRoute>} />
                        <Route path="/checkout" element={<LazyComponents.Checkout />} />
                        <Route path="/paymentComplete/:orderId" element={<LazyComponents.PaymentComplete />} />
                    </Routes>
                </Suspense>
            </div>
            {/* <ToastContainer 
                position="top-right"
                style={{ 
                    position: 'fixed', 
                    top: '26px', 
                    right: '26px',
                    zIndex: 9999
                }} 
                rtl={isRTL}
                toastStyle={{
                    height: '74px'
                }}
            /> */}

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl
                style={{ 
                    zIndex: 99999999
                }} 
                // toastStyle={{
                //     height: '74px'
                // }}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce}
            />
            
            <AuthModal
                type={modalMode}
                show={showModal}
                onClose={() => setShowModal(false)}
                setType={(value: string) => setModalMode(value)}
            />
            
            <LogoutConfirmationModal
                show={showLogoutModal}
                onClose={() => setShowLogoutModal(false)}
                onConfirm={async () => {
                    setShowLogoutModal(false);
                    try {
                        await logout();
                        navigate('/');
                    } catch (error) {
                        console.error('Logout failed:', error);
                    }
                }}
            />
        </div>
    );
};

const App: React.FC = () => {
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

    return (
        <Elements stripe={stripePromise}>
            <AppProvider>
                <Router>
                    <ScrollToTop />
                    <AppContent />
                </Router>
            </AppProvider>
        </Elements>
    );
};

export default App;

{/* <React.StrictMode>
<Elements stripe={stripePromise}>
    <AppProvider>
        <Router>
            <ScrollToTop />
            <AppContent />
        </Router>
    </AppProvider>
</Elements>
</React.StrictMode> */}